/**
 * One producer visual cue, rendered from verified evidence only. A cue that names an event or a
 * player the evidence cannot resolve renders nothing — the model directs, it never supplies facts.
 */
import type { CSSProperties } from 'react';
import { directorClock, directorTeamName, type DirectorLanguage, type DirectorMoment, type DirectorResponse, type GameEvent, type VisualCue } from '@clutch/core';
import { Headshot, TeamLogo } from '../ui/Media';
import { teamAccent } from '../ui/color';
import { pick } from './i18n';
import { cueGraphic, type Treatment } from './showplan';
import { locatedShot, momentVisual } from './moment-presentation';

type Ctx = { d: DirectorResponse; l: DirectorLanguage; plays?: readonly GameEvent[]; moment?: DirectorMoment; heading?: string; treatment: Treatment };

export function cueEvent(cue: VisualCue, c: Pick<Ctx, 'plays' | 'moment' | 'd'>): Pick<GameEvent, 'eventId' | 'seq' | 'period' | 'clockSec' | 'homeScore' | 'awayScore' | 'side' | 'personId' | 'x' | 'y' | 'points' | 'made' | 'type'> & { playerName?: string; description?: string } | undefined {
  const fromPlays = cue.eventId ? c.plays?.find((p) => p.eventId === cue.eventId && p.gameId === c.d.game.gameId && p.seq <= c.d.asOf.seq && c.d.provenance.eventIds.includes(p.eventId)) : undefined;
  if (fromPlays) return fromPlays;
  const m = c.moment;
  const visual = momentVisual(c.d, m, c.plays);
  if (visual && (!cue.eventId || visual.eventId === cue.eventId)) return { ...visual, ...(m?.player && m.player.personId === visual.personId ? { playerName: m.player.name } : {}) };
  return undefined;
}

export function cuePlayer(cue: VisualCue, c: Pick<Ctx, 'plays' | 'moment' | 'd'>): { personId: number; name: string; tricode?: string } | undefined {
  const ev = cueEvent(cue, c);
  const personId = cue.personId ?? ev?.personId ?? c.moment?.player?.personId;
  if (!personId) return undefined;
  const playerPlays = c.plays?.filter((p) => p.gameId === c.d.game.gameId && p.seq <= c.d.asOf.seq && p.personId === personId) ?? [];
  const name = (ev?.personId === personId ? ev.playerName : undefined) ?? c.d.moments.find((m) => m.seq <= c.d.asOf.seq && m.player?.personId === personId)?.player?.name ?? playerPlays.find((p) => p.playerName)?.playerName;
  if (!name) return undefined;
  // An assist/steal participant can be on the opposite team from the event's actor.
  // Never borrow that actor's team, or resolve conflicting source attribution by array order.
  const sides = new Set(playerPlays.filter((p) => p.type === 'shot' || p.type === 'freethrow').map((p) => p.side).filter((s) => s === 'home' || s === 'away'));
  if (ev?.personId === personId && ev.side) sides.add(ev.side);
  const side = sides.size === 1 ? [...sides][0] : undefined;
  return { personId, name, ...(side === 'home' || side === 'away' ? { tricode: c.d.game[side].tricode } : {}) };
}

/** Consecutive points by one side ending at the event (a "run"), from verified plays only. */
export function runAt(plays: readonly GameEvent[] | undefined, eventId: string | undefined): { side: 'home' | 'away'; points: number; against: number } | undefined {
  if (!plays?.length || !eventId) return undefined;
  const at = plays.findIndex((p) => p.eventId === eventId);
  if (at < 0) return undefined;
  let side: 'home' | 'away' | undefined;
  let pts = 0;
  for (let i = at; i >= 0; i--) {
    const p = plays[i]!;
    if (!p.points || !p.made || (p.side !== 'home' && p.side !== 'away')) continue;
    side ??= p.side;
    if (p.side !== side) break;
    pts += p.points;
  }
  return side && pts >= 4 ? { side, points: pts, against: 0 } : undefined;
}

export function CueGraphic({ cue, ...c }: Ctx & { cue: VisualCue }) {
  const g = cueGraphic(cue);
  const { d, l } = c;
  const base = { 'data-testid': 'cue-graphic', 'data-cue': cue.type, 'data-graphic': g } as const;
  if (g === 'beat') return <div className="cue cue--beat" {...base} aria-hidden="true" />;
  if (g === 'player-hero') {
    const p = cuePlayer(cue, c);
    if (!p) return null;
    const accent = p.tricode ? teamAccent(p.tricode) : 'var(--muted)';
    const style = { '--t': accent } as CSSProperties;
    return (
      <div className="cue cue--player" style={style} {...base}>
        <Headshot personId={p.personId} source={d.game.source} name={p.name} size={108} ring={accent} />
        <span className="cue__player-name"><b>{p.name}</b>{p.tricode && <small>{directorTeamName(p.tricode, l)}</small>}</span>
      </div>
    );
  }
  if (g === 'score-slam') {
    const ev = cueEvent(cue, c);
    if (!ev) return null;
    const scored = ev.side === 'home' || ev.side === 'away' ? ev.side : undefined;
    return (
      <div className={`cue cue--score${c.treatment.slam ? ' cue--slam' : ''}`} {...base} data-side={scored}>
        <span className={scored === 'away' ? 'is-hot' : ''}><TeamLogo tricode={d.game.away.tricode} size={34} /><b>{d.game.away.tricode}</b><strong>{ev.awayScore}</strong></span>
        <i>–</i>
        <span className={scored === 'home' ? 'is-hot' : ''}><strong>{ev.homeScore}</strong><b>{d.game.home.tricode}</b><TeamLogo tricode={d.game.home.tricode} size={34} /></span>
        <small>{directorClock(ev.period, ev.clockSec, l)}</small>
      </div>
    );
  }
  if (g === 'shot-marker') {
    const ev = cueEvent(cue, c);
    const playerName = ev?.playerName;
    if (!locatedShot(ev)) return null;
    const side = ev.side === 'home' || ev.side === 'away' ? ev.side : undefined;
    const x = (ev.x / 100) * 94, y = (ev.y / 100) * 50;
    return (
      <div className="cue cue--shot" style={{ '--t': side ? teamAccent(d.game[side].tricode) : 'var(--muted)' } as CSSProperties} {...base}>
        <svg viewBox="-2 -2 98 54" aria-hidden="true">
          <rect x="0" y="0" width="94" height="50" rx="1" className="cue__court" />
          <line x1="47" y1="0" x2="47" y2="50" className="cue__court" />
          <circle cx="47" cy="25" r="6" className="cue__court" />
          <path d="M0 3 H14 A23.75 23.75 0 0 1 14 47 H0 M94 3 H80 A23.75 23.75 0 0 0 80 47 H94" className="cue__court" />
          <circle cx="5.25" cy="25" r="0.9" className="cue__hoop" /><circle cx="88.75" cy="25" r="0.9" className="cue__hoop" />
          <circle cx={x} cy={y} r="5" className="cue__ping" />
          <circle cx={x} cy={y} r="1.8" className={`cue__dot${ev.made === false ? ' is-miss' : ''}`} />
        </svg>
        <small>{playerName ?? ''}{ev.points ? ` · ${ev.points}PT` : ''} · {directorClock(ev.period, ev.clockSec, l)}</small>
      </div>
    );
  }
  if (g === 'momentum') {
    const ev = cueEvent(cue, c);
    const run = d.provenance.warnings.includes('history_unreliable_derived_claims_omitted') ? undefined : runAt(c.plays?.filter((p) => p.gameId === d.game.gameId && p.seq <= d.asOf.seq), ev?.eventId ?? cue.eventId);
    if (!ev && !run) return null;
    const side = run?.side ?? (ev && ev.homeScore >= ev.awayScore ? 'home' : 'away');
    const team = d.game[side].tricode;
    return (
      <div className="cue cue--momentum" style={{ '--t': teamAccent(team) } as CSSProperties} {...base}>
        <TeamLogo tricode={team} size={40} />
        {run ? <b>{run.points}–0</b> : <b>{ev!.awayScore}–{ev!.homeScore}</b>}
        <small>{run ? pick({ en: `${directorTeamName(team, l)} run`, hi: `${directorTeamName(team, l)} का रन`, zh: `${directorTeamName(team, l)}一波攻势` }, l) : pick({ en: 'Momentum', hi: 'लय', zh: '势头' }, l)}</small>
        <span className="cue__bars" aria-hidden="true">{[0, 1, 2, 3, 4].map((i) => <i key={i} style={{ animationDelay: `${i * 90}ms` }} />)}</span>
      </div>
    );
  }
  if (g === 'call-receipt') {
    const call = d.call;
    if (!call) return null;
    const who = d.audience ? d.audience.author : pick({ en: 'You', hi: 'आप', zh: '你' }, l);
    return (
      <div className="cue cue--call" {...base}>
        <small>{pick({ en: 'Call receipt', hi: 'कॉल रसीद', zh: '预测凭证' }, l)}</small>
        <b>{pick({ en: `${who} backed ${directorTeamName(call.chosenTeam, l)}`, hi: `${who} ने ${directorTeamName(call.chosenTeam, l)} चुना`, zh: `${who}支持${directorTeamName(call.chosenTeam, l)}` }, l)}</b>
        <span>{Math.round(call.pChoiceAtLock * 100)}% · {directorClock(call.lockedAt.period, call.lockedAt.clockSec, l)}</span>
        {call.result !== 'pending' && <em className={`is-${call.result}`}>{call.result === 'correct' ? pick({ en: 'Called it', hi: 'सही कॉल', zh: '预测正确' }, l) : call.result === 'incorrect' ? pick({ en: 'Missed', hi: 'चूक गए', zh: '未中' }, l) : '—'}</em>}
      </div>
    );
  }
  if (g === 'reaction') {
    if (!d.takeIds.length) return null;
    return <div className="cue cue--reaction" {...base}><small>{pick({ en: 'Your reaction', hi: 'आपकी प्रतिक्रिया', zh: '你的反应' }, l)}</small></div>;
  }
  if (g === 'title-card') {
    return <div className="cue cue--title" {...base}><b>{c.heading ?? ''}</b></div>;
  }
  return null;
}
